import { type IOrderDraftRepository } from '../../interfaces/IOrderDraftRepository'

export class InitializeOrderDraftOrgCategoriesUseCase {
  constructor(
    private readonly deps: {
      getRequestablePrimaryCategory: IOrderDraftRepository['getRequestablePrimaryCategory']
      getRequestableSecondaryCategories: IOrderDraftRepository['getRequestableSecondaryCategories']
    }
  ) {}

  // Returns a requestable secondary category id, or null if there are no requestable secondary categories
  execute = async ({
    userId,
    userPrimaryCategoryId, // User's home location
    userSecondaryCategoryId, // User's home department
  }: {
    userId: number
    userPrimaryCategoryId: Nullable<number>
    userSecondaryCategoryId: Nullable<number>
  }): Promise<Nullable<number>> => {
    let primaryCategoryId: Nullable<number> = null

    const requestablePrimaryCategories =
      await this.deps.getRequestablePrimaryCategory(userId, {
        page: 1,
        pageSize: 9999, // Get all to check if the user's primary category is requestable
      })

    const isUserPrimaryCategoryRequestable =
      userPrimaryCategoryId &&
      requestablePrimaryCategories.data.some(
        (p) => p.id === userPrimaryCategoryId
      )

    if (isUserPrimaryCategoryRequestable) {
      // Use the user's primary category if it is requestable
      primaryCategoryId = userPrimaryCategoryId
    } else {
      // Default to the first available primary category if the user's primary category is not requestable
      primaryCategoryId = requestablePrimaryCategories.data[0]?.id ?? null
    }

    // Return early with null secondary category id if there are no requestable primary categories
    if (!primaryCategoryId) {
      return null
    }

    const requestableSecondaryCategories =
      await this.deps.getRequestableSecondaryCategories(
        userId,
        primaryCategoryId,
        {
          page: 1,
          pageSize: 9999, // Get all to check if the user's secondary category is requestable
        }
      )

    const isUserSecondaryCategoryRequestable =
      userSecondaryCategoryId &&
      requestableSecondaryCategories.data.some(
        (s) => s.id === userSecondaryCategoryId
      )

    if (isUserSecondaryCategoryRequestable) {
      // Use the user's secondary category if it is requestable
      return userSecondaryCategoryId
    } else {
      // Default to the first available secondary category if the user's secondary category is not requestable
      return requestableSecondaryCategories.data[0]?.id ?? null
    }
  }
}
